const promises = {};

const serializer = {
  serialize: data => {
    try {
      return JSON.stringify(data);
    } catch (err) {
      // data is already JSON - noop
      console.debug('failed to stringify', data);
    }
    return data;
  },
  unserialize: data => {
    try {
      return JSON.parse(data);
    } catch (err) {
      // data is already JSON - noop
      console.debug('failed to parse', data);
    }
    return data;
  }
};
/**
 * Encapsulates the `postMessage` call to iOS or Android
 * Optionally `bind`s the iOS or Android `postMessage` handler based on it's existence
 * @param  {} name - message handler name in `postMessage`
 * @return {string} `bind`ed message handler to be invoked
 */
function messageHandler(name) {
  const androidHandler = window?.android?.[name]?.bind?.(window.android);
  const iOSHandler = window?.webkit?.messageHandlers?.[
    name
  ]?.postMessage?.bind?.(window.webkit.messageHandlers[name]);
  console.debug('messageHandler', name);
  return iOSHandler || androidHandler;
}

export const createPromise = (promiseName, data) => new Promise(async (resolve, reject) => {
  const handler = messageHandler(promiseName);
  if (!handler) {
    /* eslint-disable prefer-promise-reject-errors */
    return reject({
      error: new Error(
        `${promiseName} is not currently implemented by native`
      )
    });
  }
  promises[promiseName] = { resolve, reject };
  console.debug('createPromise - invoke handler', handler, data, promises);
  if (data) {
    return handler(serializer.serialize(data));
  }
  // INFO: iOS will not invoke `postMessage`s without data being passed
  // To unify the behavior between iOS and Android, Android method will
  // always be defined to accept an optional string (String?).
  // `undefined` get serialized to a string and indicate the intent that no data is being passed.
  return handler(undefined);
});

/**
 * Should be consumed by native to resolve/reject a pending promise
 * @param {string} promiseName
 * @param {data: object, error: object} response
 */
export const resolvePromise = (promiseName, { data, error }) => {
  console.debug('resolvePromise:', { promiseName, data, error });
  if (!promises[promiseName]) {
    console.debug('!promises[promiseName]:', promiseName, promises);
    return;
  }
  if (error) {
    console.debug('error', error);
    promises[promiseName].reject({ data, error });
  } else {
    console.debug('resolve', data);
    promises[promiseName].resolve({ data: serializer.unserialize(data) });
  }
  delete promises[promiseName];
};

export const PROMISE_NAMES = {
  getNativeParams: 'getNativeParams',
  setNativeParams: 'setNativeParams',
  // STATUS
  getStatusInformation: 'getStatusInformation',
  // SECURE STORAGE
  deleteSecureData: 'deleteSecureData',
  getSecureData: 'getSecureData',
  setSecureData: 'setSecureData',
  // SECURE STORAGE - BIOMETRIC
  authenticateWithBiometric: 'authenticateWithBiometric',
  disableBiometric: 'disableBiometric',
  setBiometricsSecureData: 'setBiometricsSecureData'
};
export function authenticateWithBiometric() {
  return createPromise(PROMISE_NAMES.authenticateWithBiometric);
}
export function deleteSecureData() {
  return createPromise(PROMISE_NAMES.deleteSecureData);
}
export function disableBiometric() {
  return createPromise(PROMISE_NAMES.disableBiometric);
}
export function getNativeParams() {
  return createPromise(PROMISE_NAMES.getNativeParams).catch(() => {
    // INFO: maintain this for backward compatibility
    const NATIVE_PARAMS = window?.BOTTOMLINE?.NATIVE_PARAMS || {};
    // INFO: we intentionally don't spread here to prevent loops in a fetch hook
    return { data: NATIVE_PARAMS };
  });
}
export function getSecureData() {
  return createPromise(PROMISE_NAMES.getSecureData);
}
export function getStatusInformation() {
  return createPromise(PROMISE_NAMES.getStatusInformation);
}
export function setBiometricsSecureData(data) {
  return createPromise(PROMISE_NAMES.setBiometricsSecureData, data);
}
export function setNativeParams(data) {
  return createPromise(PROMISE_NAMES.setNativeParams).catch(() => {
    // INFO: maintain this for backward compatibility
    const windowNativeParams = window?.BOTTOMLINE?.NATIVE_PARAMS || {};
    Object.entries(data).forEach(([key, value]) => {
      windowNativeParams[key] = value;
      return windowNativeParams;
    });
    return Promise.resolve({ ...windowNativeParams });
  });
}
export function setSecureData(data) {
  return createPromise(PROMISE_NAMES.setSecureData, data);
}

export const OS_TYPES = Object.freeze({
  ANDROID: 'android',
  iOS: 'iOS'
});

// INFO: add `resolvePromise` to the object shared with native.
// This enables native to resolve promises after native code is invoked.
setNativeParams({ resolvePromise });

export default {
  PROMISE_NAMES
};
